import { useState, useRef, useCallback, useEffect } from 'react';
import { devicesInPolygon } from '../utils/mapPolygonSelection';

/**
 * Point-by-point polygon drawing on a Google Map (same interaction as backhaul DeviceMapView).
 *
 * @param {object} params
 * @param {React.MutableRefObject} params.mapRef - ref set in GoogleMap onLoad
 * @param {Array} params.devicesWithLocation - devices with lat/lng
 * @param {(devices: Array, coordinates: Array) => void} params.onSelectionComplete
 * @param {(message: string) => void} [params.onEmptySelection]
 */
export function useMapPolygonDrawing({
  mapRef,
  devicesWithLocation,
  onSelectionComplete,
  onEmptySelection,
}) {
  const [drawingMode, setDrawingMode] = useState(null);
  const [polygons, setPolygons] = useState([]);

  const currentPathRef = useRef([]);
  const drawingPolygonRef = useRef(null);
  const drawingPolylineRef = useRef(null);
  const drawingListenersRef = useRef([]);

  const toggleDrawingMode = useCallback(() => {
    setDrawingMode((prev) => (prev ? null : 'polygon'));
  }, []);

  const clearPolygons = useCallback(() => {
    setPolygons([]);
  }, []);

  const handlePolygonComplete = useCallback((coordinates) => {
    const matched = devicesInPolygon(devicesWithLocation, coordinates);
    if (matched.length > 0) {
      onSelectionComplete?.(matched, coordinates);
    } else {
      onEmptySelection?.('No devices found inside the polygon');
    }
    setPolygons((prev) => [...prev, { coordinates, id: Date.now() }]);
    setDrawingMode(null);
  }, [devicesWithLocation, onSelectionComplete, onEmptySelection]);

  useEffect(() => {
    if (!mapRef.current || !drawingMode) {
      drawingListenersRef.current.forEach((listener) => {
        if (listener && listener.remove) listener.remove();
      });
      drawingListenersRef.current = [];
      if (drawingPolygonRef.current) {
        drawingPolygonRef.current.setMap(null);
        drawingPolygonRef.current = null;
      }
      if (drawingPolylineRef.current) {
        drawingPolylineRef.current.setMap(null);
        drawingPolylineRef.current = null;
      }
      currentPathRef.current = [];
      return undefined;
    }

    const map = mapRef.current;

    const isNearPoint = (point1, point2) => {
      const projection = map.getProjection();
      if (!projection) return false;
      const point1Pixel = projection.fromLatLngToPoint(new window.google.maps.LatLng(point1.lat, point1.lng));
      const point2Pixel = projection.fromLatLngToPoint(new window.google.maps.LatLng(point2.lat, point2.lng));
      if (!point1Pixel || !point2Pixel) return false;
      const scale = Math.pow(2, map.getZoom());
      const pixelDistance = Math.sqrt(
        Math.pow((point1Pixel.x - point2Pixel.x) * scale, 2)
        + Math.pow((point1Pixel.y - point2Pixel.y) * scale, 2),
      );
      return pixelDistance < 15;
    };

    const mouseMoveListener = map.addListener('mousemove', (e) => {
      if (!e.latLng || currentPathRef.current.length === 0) return;
      const currentPoint = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      const previewPath = [...currentPathRef.current, currentPoint];
      if (window.google && window.google.maps) {
        if (!drawingPolylineRef.current) {
          drawingPolylineRef.current = new window.google.maps.Polyline({
            path: previewPath,
            strokeColor: '#1976d2',
            strokeOpacity: 0.7,
            strokeWeight: 2,
            map,
            clickable: false,
          });
        } else {
          drawingPolylineRef.current.setPath(previewPath);
        }
      }
    });

    const clickListener = map.addListener('click', (e) => {
      if (!e.latLng) return;
      const point = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      if (currentPathRef.current.length >= 3 && isNearPoint(point, currentPathRef.current[0])) {
        handlePolygonComplete(currentPathRef.current);
        currentPathRef.current = [];
        if (drawingPolygonRef.current) {
          drawingPolygonRef.current.setMap(null);
          drawingPolygonRef.current = null;
        }
        if (drawingPolylineRef.current) {
          drawingPolylineRef.current.setMap(null);
          drawingPolylineRef.current = null;
        }
        return;
      }
      currentPathRef.current.push(point);
      if (window.google && window.google.maps) {
        if (drawingPolygonRef.current) {
          drawingPolygonRef.current.setMap(null);
        }
        drawingPolygonRef.current = new window.google.maps.Polyline({
          path: currentPathRef.current,
          strokeColor: '#1976d2',
          strokeOpacity: 1.0,
          strokeWeight: 3,
          map,
          clickable: false,
        });
      }
    });

    drawingListenersRef.current = [mouseMoveListener, clickListener];

    return () => {
      drawingListenersRef.current.forEach((listener) => {
        if (listener && listener.remove) listener.remove();
      });
      drawingListenersRef.current = [];
      if (drawingPolygonRef.current) {
        drawingPolygonRef.current.setMap(null);
        drawingPolygonRef.current = null;
      }
      if (drawingPolylineRef.current) {
        drawingPolylineRef.current.setMap(null);
        drawingPolylineRef.current = null;
      }
      currentPathRef.current = [];
    };
  }, [drawingMode, handlePolygonComplete, mapRef]);

  return {
    drawingMode,
    toggleDrawingMode,
    polygons,
    clearPolygons,
    isDrawing: drawingMode === 'polygon',
  };
}
